// IGN Géoplateforme geocoding service, which replaced api-adresse.data.gouv.fr
// (deprecated since 2026-01-31). Free, no key, CORS-enabled. Only the text the
// patient is typing is sent — never any other patient identifier.
const GEOCODING_SEARCH_URL = 'https://data.geopf.fr/geocodage/search';

export const GEOCODING_MIN_QUERY_LENGTH = 3;
const RESULT_LIMIT = 5;

type GeocodingFeature = {
  properties: {
    label: string;
    name: string;
    postcode?: string;
    city?: string;
    citycode?: string;
    context?: string;
    type?: 'housenumber' | 'street' | 'locality' | 'municipality';
  };
};

export type AddressSuggestion = {
  label: string;
  street: string;
  postalCode: string;
  city: string;
};

export type CommuneSuggestion = {
  name: string;
  inseeCode: string;
  // e.g. "2A, Corse-du-Sud, Corse", to tell homonymous communes apart.
  context: string;
};

const search = async (
  params: Record<string, string>,
  signal?: AbortSignal,
): Promise<GeocodingFeature[]> => {
  const response = await fetch(
    `${GEOCODING_SEARCH_URL}?${new URLSearchParams({
      ...params,
      limit: String(RESULT_LIMIT),
    })}`,
    { signal },
  );
  if (!response.ok) throw new Error(`Geocoding failed: ${response.status}`);
  const { features } = (await response.json()) as {
    features?: GeocodingFeature[];
  };
  return features ?? [];
};

export const searchAddresses = async (
  query: string,
  signal?: AbortSignal,
): Promise<AddressSuggestion[]> => {
  // No type filter so streets and lieux-dits without a number still match;
  // a bare municipality is not a usable postal address.
  const features = await search({ q: query, autocomplete: '1' }, signal);
  return features.flatMap(({ properties }) =>
    properties.type !== 'municipality' && properties.postcode && properties.city
      ? [
          {
            label: properties.label,
            street: properties.name,
            postalCode: properties.postcode,
            city: properties.city,
          },
        ]
      : [],
  );
};

export const searchCommunes = async (
  query: string,
  signal?: AbortSignal,
): Promise<CommuneSuggestion[]> => {
  const features = await search(
    { q: query, autocomplete: '1', type: 'municipality' },
    signal,
  );
  return features.flatMap(({ properties }) =>
    properties.citycode
      ? [
          {
            name: properties.name,
            inseeCode: properties.citycode,
            context: properties.context ?? '',
          },
        ]
      : [],
  );
};
